import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import styles from './Navigation.module.css';

/**
 * The site splits by audience, so the two pages read as one control: a
 * segmented switch rather than a list of links, the same pattern as
 * oneasia-corp's personal/business switch.
 *
 * "Tenants" points at `/` rather than `/#tenants` — the whole front page is the
 * tenant track, so the switch should land at the top of it, not mid-page.
 */
const TRACKS = [
  { path: '/', labelKey: 'nav.tenants' },
  { path: '/management', labelKey: 'nav.management' },
];

const Navigation = () => {
  const { pathname } = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useTranslation('common');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const path = pathname.replace(/\/+$/, '').toLowerCase();
  /* The VDS brief has its own header. No Tenants / Management switch. */
  const onVds = path === '/investors';

  const closeMobileMenu = () => setMobileMenuOpen(false);

  /* Hide this bar once the brief leaves the top so the section map can sit
     flush. Other routes keep the sticky header. */
  useEffect(() => {
    const root = document.documentElement;
    if (!onVds) {
      delete root.dataset.header;
      root.style.scrollPaddingTop = '';
      return;
    }

    root.style.scrollPaddingTop = '0px';
    let jumping = false;
    let jumpTimer = 0;

    const hide = (hidden: boolean) => {
      if (hidden) root.dataset.header = 'hidden';
      else delete root.dataset.header;
    };
    const sync = () => {
      if (jumping) return;
      hide(window.scrollY > 16);
    };
    const finishJump = () => {
      jumping = false;
      window.clearTimeout(jumpTimer);
      sync();
    };
    const prepareJump = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest('a[href^="#"]');
      if (!link) return;
      const id = link.getAttribute('href')?.slice(1);
      const section = id ? document.getElementById(id) : null;
      if (!section) return;
      event.preventDefault();
      jumping = true;
      hide(true);
      const next = `#${id}`;
      if (window.location.hash !== next) history.pushState(null, '', next);
      window.clearTimeout(jumpTimer);
      jumpTimer = window.setTimeout(finishJump, 1500);
      requestAnimationFrame(() => {
        section.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    };

    sync();
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('scrollend', finishJump);
    document.addEventListener('click', prepareJump, true);
    return () => {
      window.removeEventListener('scroll', sync);
      window.removeEventListener('scrollend', finishJump);
      document.removeEventListener('click', prepareJump, true);
      window.clearTimeout(jumpTimer);
      delete root.dataset.header;
      root.style.scrollPaddingTop = '';
    };
  }, [onVds]);

  const isActive = (path: string) => pathname === path;

  const handleLogout = () => {
    logout();
    closeMobileMenu();
  };

  return (
    <>
      <nav className={styles.nav}>
        <div className={styles.inner}>
          {onVds ? (
            <Link to="/investors" className={styles.wordmark}>
              <span className={styles.name}>VDS</span>
            </Link>
          ) : (
            <a href="#top" className={styles.wordmark}>
              <span className={styles.name}>
                STACKCOMBO<i className={styles.dot} />
              </span>
              <span className={styles.tagline}>{t('brand.tagline')}</span>
            </a>
          )}

          {!onVds && (
            <div className={styles.trackSwitch} role="group" aria-label={t('nav.audience')}>
              {TRACKS.map(({ path, labelKey }) => (
                <Link
                  key={path}
                  to={path}
                  className={`${styles.trackLink} ${isActive(path) ? styles.trackActive : ''}`}
                  aria-current={isActive(path) ? 'page' : undefined}
                >
                  {t(labelKey)}
                </Link>
              ))}
            </div>
          )}

          <div className={styles.authSection}>
            {!onVds && (
              <div className={styles.navLanguage}>
                <LanguageSwitcher />
              </div>
            )}
            {isAuthenticated && user && (
              <>
                <span className={styles.userEmail}>{user.email}</span>
                <button onClick={handleLogout} className={styles.logoutBtn}>
                  {t('auth.logout')}
                </button>
              </>
            )}
          </div>

          <button
            className={`${styles.hamburger} ${mobileMenuOpen ? styles.hamburgerOpen : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={t('nav.menu')}
            aria-expanded={mobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      <div className={`${styles.mobileMenu} ${mobileMenuOpen ? styles.mobileMenuOpen : ''}`}>
        <ul>
          {!onVds && TRACKS.map(({ path, labelKey }) => (
            <li key={path}>
              <Link
                to={path}
                className={isActive(path) ? styles.mobileActive : ''}
                aria-current={isActive(path) ? 'page' : undefined}
                onClick={closeMobileMenu}
              >
                {t(labelKey)}
              </Link>
            </li>
          ))}
          {!onVds && (
            <li className={styles.mobileLanguage}>
              <span className={styles.mobileLanguageLabel}>{t('nav.language')}</span>
              <LanguageSwitcher variant="stacked" onSelect={closeMobileMenu} />
            </li>
          )}
          {isAuthenticated && user && (
            <li className={styles.mobileAuth}>
              <span className={styles.userEmail}>{user.email}</span>
              <button onClick={handleLogout} className={styles.logoutBtn}>
                {t('auth.logout')}
              </button>
            </li>
          )}
        </ul>
      </div>
    </>
  );
};

export default Navigation;
